/**
 * Tracking → Plan versus now. What happened to the trimester's plan.
 *
 * ⚠️ THIS REPLACES "PLANNING ACCURACY", which never worked for anybody but the
 * person who set it up. That chart compared three hand-maintained sheet cells
 * against a target held in **localStorage** — so the target was per browser:
 * committed on one laptop, invisible on every other machine and in every other
 * colleague's session, where the tab showed only "commit a trimester target to
 * see this". Both sides of this comparison are on the server and the same for
 * everyone: the frozen baseline (`taDemandSnapshot.js`) and the live HDB.
 *
 * WHY THE BUCKETS ARE SPLIT THIS FINELY. Delivering the plan and re-cutting it
 * look identical if you only diff the totals. A role that was filled, a role put
 * on hold and a role pushed into next year all leave the trimester's demand —
 * but only one of them is good news. So each is counted on its own, and the row
 * reads as an account of the trimester rather than a net number.
 *
 * The buckets balance exactly:
 *   plan − filled − on hold − re-dated − undated − dropped + added + moved in
 *     = still to recruit
 */
import type { PlanVsNow, TrackedRole } from '../../../types/taTracking';
import { Section, WhatThisCounts, roleWord } from './trackingUi';
import { PlanWaterfall } from './PlanWaterfall';

interface Step {
  key: string;
  label: string;
  sign: -1 | 1;
  n: number;
  roles: TrackedRole[];
  why: string;
}

export function PlanVsNowSection({
  data,
  takenAt,
  takenBy,
  label,
  windowEndLabel,
  noticeMonths,
}: {
  data: PlanVsNow;
  takenAt: string;
  takenBy: string;
  label: string;
  /**
   * The hiring window's last month, already formatted — e.g. "March 2027".
   *
   * ⚠️ Passed in rather than written into the copy. These definitions named
   * January and March outright, which is only true of T3: every other trimester
   * would have been given guidance about months outside its own window.
   */
  windowEndLabel: string;
  noticeMonths: number;
}) {
  const c = data.counts;
  const steps = ([
    {
      key: 'filled',
      label: 'Filled',
      sign: -1,
      n: c.filled,
      roles: data.filled,
      why: 'Offer accepted. The seat has left the plan because it is done.',
    },
    {
      key: 'onHold',
      label: 'Put on hold',
      sign: -1,
      n: c.onHold,
      roles: data.onHold,
      why: 'Paused on purpose. It gets a new start date when recruiting resumes, so it is not counted as demand meanwhile.',
    },
    {
      key: 'redated',
      label: 'Moved past the hiring window',
      sign: -1,
      n: c.redated,
      roles: data.redated,
      why: `Still to be hired, but the start date has been pushed beyond the hiring window — this trimester plus the ${noticeMonths}-month notice period, which runs to ${windowEndLabel}. A start inside that window is still THIS trimester's work, because it is offered about a notice period before the first day; only a start past ${windowEndLabel} leaves it.`,
    },
    {
      key: 'undated',
      label: 'Lost its start date',
      sign: -1,
      n: c.undated,
      roles: data.undated,
      why: 'Still real demand, but with no date it belongs to no trimester. Put a planned start on the row.',
    },
    {
      key: 'dropped',
      label: 'Cancelled or gone',
      sign: -1,
      n: c.dropped,
      roles: data.dropped,
      why: 'Cancelled in the HDB, or the row is no longer there at all.',
    },
    {
      key: 'addedBackfill',
      label: 'Added — backfills',
      sign: 1,
      n: c.addedBackfill,
      roles: data.added.filter((r) => r.reason === 'backfill'),
      why: 'Raised since the baseline because somebody left. Expected to some degree every trimester — the question is how many.',
    },
    {
      key: 'addedNew',
      label: 'Added — new roles',
      sign: 1,
      n: c.addedNew + c.addedUnknown,
      roles: data.added.filter((r) => r.reason !== 'backfill'),
      why: 'Raised since the baseline and not replacing anyone. This is the plan being re-cut, not delivered. Roles whose reason column is blank are counted here rather than as backfills.',
    },
    {
      key: 'movedIn',
      label: 'Pulled into the window',
      sign: 1,
      n: c.movedIn,
      roles: data.movedIn,
      why: 'Already in the plan, but dated beyond the hiring window — pulled forward into it since the baseline was frozen.',
    },
  ] satisfies Step[]).filter((s) => s.n > 0);

  const stillToRecruit =
    c.plan - c.filled - c.onHold - c.redated - c.undated - c.dropped + c.added + c.movedIn;
  const drift = c.added + c.movedIn - c.dropped;

  return (
    <Section
      title="Plan versus now"
      aside={
        <>
          Baseline: {label} · {new Date(takenAt).toLocaleDateString('en-GB')}
          {takenBy ? ` · ${takenBy.split('@')[0]}` : ''}
        </>
      }
    >
      {/*
        The chart is the SHAPE, the table is the record. Both, not one: the
        waterfall answers "which step moved the plan" at a glance and cannot be
        read off a column of signed numbers, while the exact figures and the
        role lists behind each bucket are what somebody acts on.
      */}
      <PlanWaterfall counts={c} stillToRecruit={stillToRecruit} />

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[420px] text-sm">
          <tbody>
            <Row
              label="Planned for this trimester"
              value={c.plan}
              bold
              why={`Roles the frozen baseline had starting inside the HIRING WINDOW — this trimester plus the ${noticeMonths}-month notice period, so every start up to ${windowEndLabel} counts — including any already overdue when it was taken. On-hold and undated rows are not in it.`}
            />
            {steps.map((s) => (
              <Row
                key={s.key}
                label={s.label}
                value={s.n}
                sign={s.sign}
                roles={s.roles}
                why={s.why}
                indent
              />
            ))}
            <Row
              label="Still to recruit"
              value={stillToRecruit}
              bold
              top
              testId="plan-vs-now-total"
              why="What is left: live HDB roles that are open or backlogged and starting inside the hiring window."
            />
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-xs text-gray-500">
        {drift === 0
          ? 'The plan has not grown since it was frozen.'
          : `The plan has ${drift > 0 ? 'grown' : 'shrunk'} by ${Math.abs(drift)} since it was frozen — ${
              c.added
            } added, ${c.movedIn} pulled forward, ${c.dropped} cancelled.`}
      </p>

      {data.unjoinable > 0 && (
        <p className="mt-1.5 text-xs text-amber-800">
          {roleWord(data.unjoinable)} carry no <b>Position ID</b>, so they cannot be matched between
          the baseline and today and are left out of this comparison — never counted as new.
        </p>
      )}
    </Section>
  );
}

function Row({
  label,
  value,
  sign,
  roles,
  why,
  bold,
  top,
  indent,
  testId,
}: {
  label: string;
  value: number;
  sign?: -1 | 1;
  roles?: TrackedRole[];
  why: string;
  bold?: boolean;
  top?: boolean;
  indent?: boolean;
  testId?: string;
}) {
  const tone = sign === 1 ? 'text-rose-700' : sign === -1 ? 'text-gray-600' : 'text-gray-900';
  return (
    <tr data-testid={testId} className={top ? 'border-t border-gray-200' : undefined}>
      <td className={`py-1.5 pr-3 ${indent ? 'pl-4' : ''} ${bold ? 'font-semibold' : ''}`}>
        {/*
          The ⓘ sits INSIDE the summary, beside the label.
          `<details>` is a block, so a sibling span after it wrapped onto its own
          line under every row — a column of orphaned info buttons. It is the
          same control as the KPI tiles, and for the same reason: `title` shows
          on hover with a mouse and nowhere else, so from a keyboard or a touch
          screen these definitions did not exist at all.
        */}
        {roles && roles.length > 0 ? (
          <details>
            <summary className="cursor-pointer text-gray-700 hover:text-gray-900">
              <span className="inline-flex items-center gap-1 align-middle">
                {label}
                <WhatThisCounts label={label} why={why} />
              </span>
            </summary>
            <ul className="mt-1 space-y-0.5 pl-1 text-xs text-gray-500">
              {roles.map((r) => (
                <li key={`${r.positionId}-${r.title}`}>
                  {r.title}
                  {r.unit && <span className="text-gray-400"> · {r.unit}</span>}
                  {r.month && <span className="text-gray-400"> · {r.month}</span>}
                </li>
              ))}
            </ul>
          </details>
        ) : (
          <span
            className={`inline-flex items-center gap-1 ${bold ? 'text-gray-900' : 'text-gray-700'}`}
          >
            {label}
            <WhatThisCounts label={label} why={why} />
          </span>
        )}
      </td>
      <td className={`py-1.5 text-right tabular-nums ${bold ? 'font-semibold text-gray-900' : tone}`}>
        {sign === 1 ? '+' : sign === -1 ? '−' : ''}
        {value}
      </td>
    </tr>
  );
}

/** Shown in place of the comparison when nobody has frozen a baseline yet. */
export function NoBaseline() {
  return (
    <Section title="Plan versus now">
      <p className="mt-3 text-sm text-gray-500">
        Nothing to compare against yet. Airtable keeps no field-level history, so a start date that
        moves leaves no trace — the trimester's plan only survives if somebody freezes a copy of it.
        Take one with <b>Freeze baseline</b> on <b>Planning → Hiring demand</b>, or import the
        Airtable export from the day the trimester opened.
      </p>
      <p className="mt-2 text-xs text-gray-400">
        Until then the target above is today's table, not the plan — which is why it is labelled
        that way.
      </p>
    </Section>
  );
}
